import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, MenuItem, Select,
  FormControl, InputLabel, FormControlLabel, Switch, Stack, Alert,
  Chip, IconButton, Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import { AuthContext } from '../../../context/logeduser';
import {
  useFinancialYears, useMyDeclaration, useSaveDeclaration,
  useSubmitDeclaration, useUploadProof, useDeleteProof,
} from '../../../hooks/useForm16';
import PageHeader from '../PageHeader';
import ProofLink from '../../common/ProofLink';
import { SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';

// ---- Visual tokens (shared with the employee Home, Attendance & Payslip dashboards) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue

// Soft tinted status-chip styles keyed to declaration status.
const STATUS_CHIP_SX = {
  APPROVED: { bgcolor: '#f0fdf4', color: '#16a34a' },
  REJECTED: { bgcolor: '#fef2f2', color: '#ef4444' },
  SUBMITTED: { bgcolor: '#eef4ff', color: '#2f6bff' },
};

// [fieldKey, label, proofSectionCode]
const SEC80C_FIELDS = [
  ['sec_80c_lic', 'LIC Premium', '80C_LIC'],
  ['sec_80c_ppf', 'PPF', '80C_PPF'],
  ['sec_80c_elss', 'ELSS / Mutual Fund', '80C_ELSS'],
  ['sec_80c_nsc', 'NSC', '80C_NSC'],
  ['sec_80c_tuition', 'Children Tuition Fees', '80C_TUITION'],
  ['sec_80c_home_principal', 'Home Loan Principal', '80C_HOME'],
  ['sec_80c_other', 'Other 80C', '80C_OTHER'],
];

const sumOf = (obj, keys) =>
  keys.reduce((acc, k) => acc + Number(obj[k] || 0), 0);

// Just the file name from a (possibly pre-signed S3) URL: drop the ?X-Amz-...
// query string, take the last path segment, and decode %20-style escapes.
const fileNameFromUrl = (url) => {
  const path = String(url || '').split('?')[0];
  const last = path.split('/').pop() || path;
  try { return decodeURIComponent(last); } catch { return last; }
};

export default function DeclarationsEmp() {
  const { authData } = useContext(AuthContext);
  const employeeNo = authData?.employee_no;
  const { data: fys = [] } = useFinancialYears();
  const [fyId, setFyId] = useState('');

  useEffect(() => {
    if (fyId || !fys.length) return;
    let remembered = null;
    try { remembered = localStorage.getItem('declEmpLastFyId'); } catch { /* ignore */ }
    if (remembered && fys.some((f) => String(f.id) === remembered)) {
      setFyId(Number(remembered));
      return;
    }
    const today = new Date();
    const current = fys.find((f) => {
      const start = new Date(f.start_date);
      const end = new Date(f.end_date);
      return today >= start && today <= end;
    });
    setFyId((current || fys[0]).id);
  }, [fys, fyId]);

  const { data: decl, refetch } = useMyDeclaration(employeeNo, fyId);
  const save = useSaveDeclaration(employeeNo, fyId);
  const submit = useSubmitDeclaration(employeeNo, fyId);
  const uploadProof = useUploadProof();
  const deleteProof = useDeleteProof();
  const [form, setForm] = useState(null);
  const [files, setFiles] = useState({});            // { [sectionCode]: File }
  const [pendingSection, setPendingSection] = useState(null);
  const [inputNonce, setInputNonce] = useState({});  // bump to clear a file input after upload
  const [toast, setToast] = useState(null);          // { severity, msg }

  useEffect(() => {
    if (decl) setForm(decl);
  }, [decl]);

  if (!employeeNo) return <Alert severity="error">Not logged in.</Alert>;
  if (!form) return (
    <Box sx={{ pb: { xs: 4, md: 2 } }}>
      <Alert severity="info" sx={{ borderRadius: '12px' }}>Loading…</Alert>
    </Box>
  );

  const set = (k) => (e) => setForm({
    ...form,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  });

  // Choose-file + Upload controls for a section. Sits on the field's top row
  // (next to the amount input); the uploaded-file list renders separately below.
  // Plain function (not a component) so re-renders don't remount the file input.
  const renderProofControls = (code) => {
    const file = files[code];
    const uploading = (uploadProof.isPending || save.isPending) && pendingSection === code;
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap" sx={{ flexShrink: 0 }}>
        <Button
          component="label"
          size="small"
          variant="outlined"
          startIcon={<AttachFileIcon fontSize="small" />}
          disabled={!editable}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb', whiteSpace: 'nowrap' }}
        >
          {file ? 'Change' : 'Choose file'}
          <input
            key={`file-${code}-${inputNonce[code] || 0}`}
            type="file"
            hidden
            onChange={(e) =>
              setFiles((prev) => ({ ...prev, [code]: e.target.files?.[0] || null }))}
          />
        </Button>
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<CloudUploadIcon fontSize="small" />}
          disabled={!file || !editable || uploading}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '12px',
            background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
            '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
          }}
          onClick={() => {
            setPendingSection(code);
            // Persist any unsaved amount/field edits first — otherwise the refetch after a
            // successful upload overwrites local `form` state (via the `decl` effect above)
            // with the server's stale values, silently discarding whatever the user just typed
            // but hadn't saved yet.
            save.mutate(buildPayload(), {
              onSuccess: () => {
                uploadProof.mutate(
                  { declarationId: form.id, sectionCode: code, file },
                  {
                    onSuccess: () => {
                      setFiles((prev) => ({ ...prev, [code]: null }));
                      setInputNonce((prev) => ({ ...prev, [code]: (prev[code] || 0) + 1 }));
                      refetch();
                      setToast({ severity: 'success', msg: 'Proof uploaded.' });
                    },
                    onError: (e) => setToast({ severity: 'error', msg: `Upload failed: ${errorText(e)}` }),
                    onSettled: () => setPendingSection(null),
                  },
                );
              },
              onError: (e) => {
                setPendingSection(null);
                setToast({ severity: 'error', msg: `Could not save before upload: ${errorText(e)}` });
              },
            });
          }}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </Stack>
    );
  };

  // Already-uploaded proofs for a section, rendered full-width BELOW the field row
  // so long filenames never squeeze the amount input. Each proof is a tidy chip
  // row (icon · name · delete); the name ellipsis-truncates with a tooltip.
  const renderProofFiles = (code) => {
    const sectionProofs = (form.proofs || []).filter((p) => p.section_code === code);
    if (sectionProofs.length === 0) return null;
    return (
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9aa1ac' }}>
          Attached · {sectionProofs.length}
        </Typography>
        {sectionProofs.map((p) => {
          const name = p.description || p.file_name || fileNameFromUrl(p.file);
          return (
            <Stack key={p.id} direction="row" spacing={1} alignItems="center"
              sx={{ px: 1, py: 0.5, borderRadius: '10px', bgcolor: '#f5f7fb', border: '1px solid #e8ecf5', minWidth: 0 }}>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: '#9aa1ac', flexShrink: 0 }} />
              <ProofLink proof={p} label={name} title={name}
                style={{ flex: 1, minWidth: 0, fontSize: 12, color: ACCENT, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
              {editable && (
                <IconButton size="small" aria-label={`Delete ${name}`} sx={{ flexShrink: 0 }}
                  onClick={() => save.mutate(buildPayload(), {
                    onSuccess: () => deleteProof.mutate(p.id, {
                      onSuccess: () => { refetch(); setToast({ severity: 'success', msg: 'Proof removed.' }); },
                      onError: (e) => setToast({ severity: 'error', msg: `Delete failed: ${errorText(e)}` }),
                    }),
                    onError: (e) => setToast({ severity: 'error', msg: `Could not save before delete: ${errorText(e)}` }),
                  })}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  // A declarable amount + its proof uploader, laid out VERTICALLY: the amount
  // field and Choose/Upload controls share the top row (top-aligned so the input
  // never floats to the middle), and the uploaded-file list sits full-width below.
  // `md` sets the grid span — default 4 (three-up dense sections); Section A passes
  // 6 for a roomier two-up.
  const renderFieldWithProof = (k, label, code, md = 4) => {
    const sel = files[code];
    return (
      <Grid item xs={12} sm={6} md={md} key={k}>
        <Box sx={{ border: '1px solid #eef0f4', borderRadius: '14px', p: 1.25, height: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <TextField size="small" type="number" label={label}
              sx={{ flex: '1 1 150px', minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              value={form[k] || ''} onChange={set(k)} disabled={!editable} />
            {renderProofControls(code)}
          </Stack>
          {sel && (
            <Typography variant="caption" color="text.secondary" noWrap
              sx={{ display: 'block', mt: 0.75, maxWidth: '100%' }} title={sel.name}>
              Selected: {sel.name}
            </Typography>
          )}
          {renderProofFiles(code)}
        </Box>
      </Grid>
    );
  };

  const sec80cTotal = sumOf(form, SEC80C_FIELDS.map(([k]) => k));
  const sec80cCapped = Math.min(150000, sec80cTotal);
  const editable = form.status !== 'APPROVED';
  const newRegime = String(form.regime || '').toUpperCase() === 'NEW';

  const buildPayload = () => {
    const payload = { ...form };
    delete payload.proofs;
    delete payload.fy_label;
    delete payload.employee_no;
    delete payload.employee_name;
    // The URL (employee_no/fy_id) already identifies the record — the backend
    // never reads these from the body. Sending them is not just redundant: if
    // `form` hasn't caught up yet after switching the FY dropdown (the new
    // FY's GET is still in flight), this would carry the OLD financial_year
    // into a PATCH aimed at the NEW FY's row, tripping the (employee,
    // financial_year) uniqueness check against the other record.
    delete payload.employee;
    delete payload.financial_year;
    // A cleared type="number" input leaves '' in local state. Sending '' for a
    // DecimalField 400s the ENTIRE PATCH (not just that field) — so drop blank
    // fields instead of sending them; the server's existing value stands.
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '' || payload[k] === null || payload[k] === undefined) {
        delete payload[k];
      }
    });
    return payload;
  };

  const onSave = () => save.mutate(buildPayload(), {
    onSuccess: () => { refetch(); setToast({ severity: 'success', msg: 'Draft saved.' }); },
    onError: (e) => setToast({ severity: 'error', msg: `Could not save: ${errorText(e)}` }),
  });

  const onSubmit = () => {
    save.mutate(buildPayload(), {
      onSuccess: () => {
        submit.mutate(undefined, {
          onSuccess: () => {
            refetch();
            setToast({ severity: 'success', msg: 'Submitted to HR for review.' });
          },
          onError: (e) => setToast({ severity: 'error', msg: `Could not submit: ${errorText(e)}` }),
        });
      },
      onError: (e) => setToast({ severity: 'error', msg: `Could not submit: ${errorText(e)}` }),
    });
  };

  // Turn an axios error into a readable message (DRF 400s return field errors).
  const errorText = (e) => {
    const d = e?.response?.data;
    if (!d) return e?.message || 'request failed';
    if (typeof d === 'string') return d;
    if (d.detail) return d.detail;
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
      .join(' · ');
  };

  return (
    <Box sx={{ pb: { xs: 4, md: 2 } }}>
      <PageHeader
        icon={<SavingsRoundedIcon />}
        title="Tax-Saving Declarations"
        subtitle="Submit your investment declarations for the financial year. HR reviews these before issuing Form 16."
        action={(
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Financial Year</InputLabel>
              <Select label="Financial Year" value={fyId} onChange={(e) => {
                const nextFyId = e.target.value;
                if (editable) {
                  save.mutate(buildPayload(), {
                    onError: (err) => setToast({
                      severity: 'error',
                      msg: `Could not save this year's edits before switching: ${errorText(err)}`,
                    }),
                  });
                }
                setFyId(nextFyId);
                try { localStorage.setItem('declEmpLastFyId', String(nextFyId)); } catch { /* ignore */ }
              }}
                MenuProps={SELECTED_MENU_PROPS}
                sx={{ borderRadius: '12px' }}>
                {fys.map((fy) => (<MenuItem key={fy.id} value={fy.id}>{fy.label}</MenuItem>))}
              </Select>
            </FormControl>
            <Chip label={form.status} sx={{ fontWeight: 700, ...(STATUS_CHIP_SX[form.status] || { bgcolor: '#eef4ff', color: '#2f6bff' }) }} />
          </Stack>
        )}
      />

      {form.status === 'REJECTED' && form.rejection_reason && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>HR rejected your declaration: {form.rejection_reason}</Alert>
      )}
      {form.status === 'APPROVED' && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>Approved. Edits are locked. Contact HR if changes are needed.</Alert>
      )}

      <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 1.5 }}>Tax Regime</Typography>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <Select value={(form.regime || 'new').toLowerCase()} onChange={set('regime')} disabled={!editable}
            MenuProps={SELECTED_MENU_PROPS}
            sx={{ borderRadius: '12px' }}>
            <MenuItem value="old">Old Regime (with deductions)</MenuItem>
            <MenuItem value="new">New Regime (concessional rates, no 80C/HRA)</MenuItem>
          </Select>
        </FormControl>
        {newRegime && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>
            Under the new regime, 80C / 80D / HRA deductions are not allowed — a ₹75,000 standard
            deduction is applied automatically. Only the items below are declarable.
          </Alert>
        )}
      </Paper>

      {!newRegime && (
        <>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={2} spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>Section 80C investments (max ₹1,50,000 combined with 80CCC + 80CCD(1))</Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>Total: <b style={{ color: '#111827' }}>{sec80cTotal.toLocaleString('en-IN')}</b> · Eligible: <b style={{ color: ACCENT }}>{sec80cCapped.toLocaleString('en-IN')}</b></Typography>
            </Stack>
            <Grid container spacing={2}>
              {SEC80C_FIELDS.map(([k, label, code]) => renderFieldWithProof(k, label, code))}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 1.5 }}>NPS / Pension</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="80CCC (Pension fund)" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} value={form.sec_80ccc || ''} onChange={set('sec_80ccc')} disabled={!editable} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="80CCD(1) — Self NPS" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} value={form.sec_80ccd_1 || ''} onChange={set('sec_80ccd_1')} disabled={!editable} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="80CCD(1B) — Additional (max 50k)" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} value={form.sec_80ccd_1b || ''} onChange={set('sec_80ccd_1b')} disabled={!editable} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label="80CCD(2) — Employer NPS" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} value={form.sec_80ccd_2 || ''} onChange={set('sec_80ccd_2')} disabled={!editable} /></Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 1.5 }}>Section 80D — Health insurance</Typography>
            <Grid container spacing={2}>
              {renderFieldWithProof('sec_80d_self', 'Self / Spouse / Children', '80D_SELF')}
              {renderFieldWithProof('sec_80d_parents', 'Parents', '80D_PARENTS')}
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel control={<Switch checked={!!form.sec_80d_parents_senior} onChange={set('sec_80d_parents_senior')} disabled={!editable} />}
                  label="Parents are senior citizens (cap 50k)" />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 1.5 }}>Other Chapter VI-A</Typography>
            <Grid container spacing={2}>
              {renderFieldWithProof('sec_80e', '80E — Education loan interest', '80E')}
              {renderFieldWithProof('sec_80g', '80G — Donations', '80G')}
              {renderFieldWithProof('sec_80tta', '80TTA — Savings interest (cap 10k)', '80TTA')}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 1.5 }}>HRA / House Property</Typography>
            <Grid container spacing={2}>
              {renderFieldWithProof('rent_paid_annual', 'Annual rent paid', 'HRA_RENT')}
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel control={<Switch checked={!!form.metro_city} onChange={set('metro_city')} disabled={!editable} />}
                  label="I live in a metro city (50% of basic vs 40%)" />
              </Grid>
              {renderFieldWithProof('sec_24b_interest', 'Sec 24(b) home-loan interest (cap 2L)', '24B')}
            </Grid>
          </Paper>
        </>
      )}

      {newRegime && (
        <>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>A. Previous Employer Details</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Salary received and TDS deducted by your previous employer this financial year (attach their Form 16 / Form 12B).
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {renderFieldWithProof('prev_employer_salary', 'Previous employer salary', 'PREV_EMP_SALARY', 6)}
              {renderFieldWithProof('prev_employer_tds', 'Previous employer TDS', 'PREV_EMP_TDS', 6)}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>B. Income from House Property</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Net income or loss from a let-out property (enter a negative value for a loss).
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {renderFieldWithProof('house_property_letout', 'Let-out property income / loss', 'HP_LETOUT')}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>C. Other Income</Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {renderFieldWithProof('other_income_interest', 'Interest income', 'OI_INTEREST')}
              {renderFieldWithProof('other_income_rental', 'Rental income', 'OI_RENTAL')}
              {renderFieldWithProof('other_income_other', 'Any other taxable income', 'OI_OTHER')}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>D. Deductions Allowed</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Standard deduction of ₹75,000 is applied automatically. Only employer NPS u/s 80CCD(2) is declarable under the new regime.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {renderFieldWithProof('sec_80ccd_2', '80CCD(2) — Employer NPS contribution', '80CCD2')}
            </Grid>
          </Paper>
        </>
      )}

      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        <Button variant="outlined" disabled={!editable || save.isPending} onClick={onSave}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' }}>
          Save Draft
        </Button>
        <Button variant="contained" disableElevation disabled={!editable || submit.isPending || save.isPending}
          onClick={onSubmit}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '12px',
            background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
            '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
          }}>
          {submit.isPending || save.isPending ? 'Sending…' : 'Submit for HR review'}
        </Button>
      </Stack>

      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.severity === 'error' ? null : 4000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setToast(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled"
            onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
